import { Box, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';

import EnterpriseRow from '../EnterpriseRow';
import { FilterCriteriaFields } from '@models/filter-criteria-fields';
import IOption from '@models/option';
import { ISearchFilter } from '@models/dto/searchCriteria.dto';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import { JSelect } from '@atoms/JSelect';
import { JTable } from '@atoms/JTable';
import { JTablePagination } from '@atoms/JTablePagination';
import { KEY_ORDER } from '@models/key-order';
import { SearchTextField } from '../../../../components/atoms/SearchTextField';
import TableColumn from '@models/table-column';
import { appendSearchFilter } from '@lib/tableFilterUtils';
import { useCities } from '@/queries/useCities';
import { useDebouncedCallback } from 'use-debounce';
import useEnterprises from '@enterprise/hooks/useEnterprises';
import { useGetEnterprises } from '@enterprise/queries/useGetEnterprises';
import { useTranslation } from 'react-i18next';

const TABLE_COLS: TableColumn[] = [
  { name: 'NAME', orderable: true, sortingField: 'enterprise.name' },
  { name: 'SIREN', orderable: true, sortingField: 'enterprise.siren' },
  { name: 'POSTAL_CODE', orderable: true, sortingField: 'city.zipCode' },
  { name: 'CITY', orderable: true, sortingField: 'city.name' },
  { name: 'STATUS', orderable: true, sortingField: 'enterprise.deleted_at' },
  {
    name: 'ESTABLISHMENT_COUNT',
    orderable: true,
    sortingField: 'establishmentsCount',
  },
  { name: 'USERS_COUNT', orderable: true, sortingField: 'usersCount' },
];

const PER_PAGE = 20;
const DEFAULT_ORDER = {
  order: KEY_ORDER.DESC,
  orderBy: 'enterprise.deleted_at',
};

const EnterprisesList: React.FC = () => {
  const { t } = useTranslation();
  const { setEnterprisesCount } = useEnterprises();

  const [cityInputValue, setCityInputValue] = useState<string>('');
  const [page, setPage] = useState<number>();
  const [order, setOrder] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>(DEFAULT_ORDER);

  const [searchFilter, setSearchFilter] = useState<ISearchFilter[]>([]);
  const { data: cities } = useCities(10, 1, cityInputValue);
  const { data } = useGetEnterprises(
    {
      page,
      limit: PER_PAGE,
      order: order.order,
      orderBy: order.orderBy,
    },
    {
      searchCriteria: searchFilter,
    },
  );

  const rows = data?.data.data;
  const meta = data?.data.meta;
  const pagesCount = data?.data.meta.itemCount;
  const isNotFound = rows?.length === 0;

  useEffect(() => {
    setEnterprisesCount(meta?.itemCount);
  }, [meta?.itemCount]);

  const debouncedAppendSearchFilter = useDebouncedCallback(
    appendSearchFilter,
    1000,
  );
  const debouncedCity = useDebouncedCallback((value) => {
    setCityInputValue(value);
  }, 1000);

  const citiesOptions = cities
    ? cities.data.data.map(({ id, name, zipCode }) => {
        return { value: id, label: `${name} / ${zipCode}` };
      })
    : [];

  const handleColumnClick = (column: string) => {
    if (order.orderBy === column) {
      // If user clicks on the same column, switch the order
      setOrder((prevOrder) => ({
        order:
          prevOrder.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      // If user clicks on a different column, update order field and use default order
      setOrder({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };

  return (
    <>
      <Stack marginY={3} direction="row" gap={2}>
        <JSelect
          data-qa="select-status"
          options={[
            { label: 'Tous', value: '' },
            { label: 'Active', value: 'true' },
            { label: 'Inactive', value: 'false' },
          ]}
          size="small"
          label="Statut"
          onChange={(event) =>
            appendSearchFilter(
              FilterCriteriaFields.ENTERPRISE_STATUS,
              event.target.value as string,
              searchFilter,
              setSearchFilter,
            )
          }
        />
        <SearchTextField
          data-qa="name"
          label="Recherche par nom"
          onChange={(value) =>
            debouncedAppendSearchFilter(
              FilterCriteriaFields.ENTERPRISE_NAME,
              value,
              searchFilter,
              setSearchFilter,
            )
          }
        />
        <SearchTextField
          data-qa="siren"
          label="Siren"
          onChange={(value) =>
            debouncedAppendSearchFilter(
              FilterCriteriaFields.ENTERPRISE_SIREN,
              value,
              searchFilter,
              setSearchFilter,
            )
          }
        />
        <JAutocomplete
          data-qa="city"
          label={t('CITY')}
          options={citiesOptions}
          onChange={(ev, newValue) =>
            appendSearchFilter(
              FilterCriteriaFields.ENTERPRISE_CITY_ID,
              (newValue as IOption)?.value || '',
              searchFilter,
              setSearchFilter,
            )
          }
          onTextChange={(val) => debouncedCity(val)}
        />
      </Stack>
      <JTable
        isNotFound={isNotFound}
        columns={TABLE_COLS}
        notFoundLabel={t('NO_ENTERPRISE_FOUND') ?? ''}
        total={pagesCount ?? 0}
        perpage={PER_PAGE}
        nbpages={pagesCount ?? 0}
        onChangeOrder={(value) => handleColumnClick(value)}
      >
        {rows?.map((row) => (
          <EnterpriseRow
            row={row}
            key={row.enterprise_id}
            data-qa={`enterprise-row-${row.enterprise_id}`}
          />
        ))}
      </JTable>

      {meta && (
        <Box mt={2}>
          <JTablePagination
            onPageChange={(value) => {
              setPage(value);
            }}
            meta={meta}
          />
        </Box>
      )}
    </>
  );
};

export default EnterprisesList;
